import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutContent } from '@/mocks/zh/comics';

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '韩漫免费网站',
  url: 'http://lczydmb.com/',
  description: '国内领先的韩国漫画在线阅读平台，致力于为韩漫爱好者提供最新最全的韩漫资源。',
  foundingDate: '2023',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '400-888-6666',
    contactType: 'customer service',
    email: 'hello@lczydmb.com',
    availableLanguage: ['Chinese'],
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: '望京SOHO T1-B座 1808室',
    addressLocality: '北京',
    addressRegion: '朝阳区',
    addressCountry: 'CN',
    postalCode: '100102',
  },
  sameAs: ['http://lczydmb.com/'],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '韩漫免费网站上的漫画都是免费的吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '是的，韩漫免费网站上的所有韩国漫画均为完全免费阅读，无需付费订阅即可享受高清无删减的漫画内容。我们致力于为广大韩漫爱好者提供无障碍的阅读体验。',
      },
    },
    {
      '@type': 'Question',
      name: '韩漫免费网站的漫画更新频率如何？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '我们的漫画更新与韩国官方保持同步，热门连载作品通常在韩国发布后的24小时内即可在平台上阅读到最新中文翻译版。每日都有多部作品更新，确保读者不会错过任何精彩内容。',
      },
    },
    {
      '@type': 'Question',
      name: '韩漫免费网站提供哪些类型的韩国漫画？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '韩漫免费网站涵盖热血、奇幻、恋爱、校园、格斗、运动、喜剧、悬疑等多种类型的韩国漫画。目前已收录超过5000部韩漫作品，覆盖主流和冷门题材，满足不同读者的阅读偏好。',
      },
    },
    {
      '@type': 'Question',
      name: '如何在韩漫免费网站上搜索我想看的漫画？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以通过网站顶部的搜索功能输入漫画名称或作者名进行搜索，也可以按分类浏览不同类型的漫画。我们还提供热门推荐和最新更新列表，帮助您快速发现感兴趣的漫画作品。',
      },
    },
    {
      '@type': 'Question',
      name: '韩漫免费网站的阅读体验如何？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '韩漫免费网站提供高清画质和流畅的滚动阅读模式，支持PC端和移动端自适应浏览。页面加载速度快，无干扰广告，为您提供沉浸式的韩漫阅读体验。',
      },
    },
  ],
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '关于我们 - 韩漫免费网站',
  description: '了解韩漫免费网站的品牌故事、团队理念和联系方式。国内领先的韩国漫画在线阅读平台。',
  url: 'http://lczydmb.com/about',
  isPartOf: {
    '@type': 'WebSite',
    name: '韩漫免费网站',
    url: 'http://lczydmb.com/',
  },
};

const breadcrumbSchemaData = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'http://lczydmb.com/' },
    { '@type': 'ListItem', position: 2, name: '关于我们' },
  ],
};

export default function About() {
  return (
    <>
      <SEOHead
        title="关于我们_韩漫免费网站_品牌介绍与联系方式-韩漫网站"
        description="韩漫免费网站是国内领先的韩国漫画在线阅读平台，成立于2023年。了解我们的品牌故事、核心价值观、用户口碑和联系方式。汇聚5000+部精选韩漫，服务300万+月活用户。"
        keywords="关于韩漫免费网站,韩漫平台介绍,韩国漫画网站介绍,韩漫联系方式"
        canonicalUrl="http://lczydmb.com/about"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchemaData) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <Navbar />

      <main className="min-h-screen pt-16">
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[{ name: '首页', path: '/' }, { name: '关于我们' }]} />

            <div className="mt-6">
              <span className="text-xs font-label text-primary-500 uppercase tracking-wider">关于我们</span>
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mt-2">
                关于<strong className="text-primary-500">韩漫世界</strong>
              </h1>
              <p className="text-sm md:text-base text-foreground-600 mt-4 max-w-2xl">
                {aboutContent.brandIntro}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mt-10">
              {aboutContent.stats.map((stat) => (
                <div key={stat.label} className="bg-background-50 rounded-xl p-6 text-center">
                  <div className="text-3xl md:text-4xl font-heading font-bold text-primary-500">{stat.value}</div>
                  <div className="text-sm text-foreground-600 mt-2">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-14">
              <div className="lg:w-1/2">
                <div className="relative overflow-hidden rounded-2xl aspect-[4/3]">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20modern%20creative%20office%20space%20with%20team%20members%20collaborating%20around%20large%20monitors%20displaying%20colorful%20comic%20illustrations%20warm%20wooden%20interior%20natural%20light%20streaming%20through%20windows%20professional%20yet%20creative%20atmosphere%20plants%20and%20art%20on%20walls&width=800&height=600&seq=about-team-office&orientation=landscape"
                    alt="韩漫免费网站团队"
                    title="韩漫免费网站团队 - 韩漫免费网站"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                    <span className="bg-background-50/20 backdrop-blur-sm text-background-50 text-xs px-3 py-1 rounded-full font-label whitespace-nowrap">专业团队</span>
                    <span className="bg-background-50/20 backdrop-blur-sm text-background-50 text-xs px-3 py-1 rounded-full font-label whitespace-nowrap">热爱漫画</span>
                    <span className="bg-background-50/20 backdrop-blur-sm text-background-50 text-xs px-3 py-1 rounded-full font-label whitespace-nowrap">精益求精</span>
                  </div>
                </div>
              </div>

              <div className="lg:w-1/2 flex flex-col justify-between">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                    我们的<strong className="text-primary-500">使命</strong>
                  </h2>
                  <div className="flex items-center gap-3 mt-3 flex-wrap">
                    <span className="border border-primary-300 text-primary-500 px-4 py-1.5 rounded-full text-sm font-label whitespace-nowrap">内容至上</span>
                    <span className="text-foreground-600 px-4 py-1.5 rounded-full text-sm font-label whitespace-nowrap">用户体验</span>
                    <span className="text-foreground-600 px-4 py-1.5 rounded-full text-sm font-label whitespace-nowrap">技术驱动</span>
                  </div>
                  <p className="text-sm text-foreground-600 mt-5 leading-relaxed">
                    {aboutContent.mission}
                  </p>
                </div>

                <div className="mt-8 bg-foreground-950 rounded-2xl p-6">
                  <span className="text-xs font-label text-accent-400 uppercase">联系我们</span>
                  <div className="space-y-3 mt-3">
                    <div className="flex items-center gap-3 text-sm text-background-100/80">
                      <i className="ri-map-pin-line text-accent-400" />
                      <span>{aboutContent.contact.address}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-background-100/80">
                      <i className="ri-phone-line text-accent-400" />
                      <span>{aboutContent.contact.phone}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-background-100/80">
                      <i className="ri-mail-line text-accent-400" />
                      <span>{aboutContent.contact.email}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-xs font-label text-accent-500 uppercase tracking-wider">核心理念</span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-2">
                我们的<strong className="text-accent-500">价值观</strong>
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {aboutContent.values.map((v, i) => (
                <div key={v.title} className="bg-background-50 rounded-xl p-6 flex flex-col items-center text-center">
                  <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className={`text-xl ${['ri-heart-3-line', 'ri-eye-line', 'ri-flashlight-line', 'ri-group-line'][i]}`} />
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">{v.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-xs font-label text-primary-500 uppercase tracking-wider">媒体 &amp; 用户口碑</span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-2">
                他们<strong className="text-primary-500">怎么说</strong>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-10">
              {aboutContent.mediaMentions.map((m) => (
                <div key={m.name} className="bg-background-100 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <i className="ri-double-quotes-l text-accent-400 text-xl" />
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">{m.quote}</p>
                  <span className="text-xs font-label font-semibold text-foreground-900">—— {m.name}</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {aboutContent.reviews.map((review) => (
                <div key={review.name} className="bg-background-100 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={review.avatar}
                      alt={review.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-sm font-label font-semibold text-foreground-800">{review.name}</div>
                      <div className="text-xs text-foreground-500">{review.role}</div>
                    </div>
                  </div>
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-400 text-xs" />
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">{review.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-xs font-label text-accent-500 uppercase tracking-wider">帮助中心</span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-2">
                常见<strong className="text-accent-500">问题</strong>
              </h2>
            </div>

            <div className="space-y-3">
              {faqSchema.mainEntity.map((faq, i) => (
                <details key={i} className="group bg-background-50 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-label text-sm text-foreground-800 font-medium list-none">
                    <span>{faq.name}</span>
                    <i className="ri-add-line group-open:rotate-45 transition-transform duration-200 text-foreground-400" />
                  </summary>
                  <div className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.acceptedAnswer.text}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}